<template>
  <div v-loading="processing">
    <hr />
    <div class="flex align-center">
      <h4 class="flex align-center" style="padding-right: 10px">
        Two-Factor Authentication
        <el-button
          v-if="mfaEnabled"
          class="custom-loading-button ml-30"
          :class="[!forcedMfaByOrg ? 'disable-hover-effect' : 'disable-hover-disabled']"
          style="padding: 10px; margin-top: 1px; font-size: 12px"
          v-loading="disableProcessing"
          :type="mfaEnabled && buttonText === 'Enabled' ? 'success' : 'danger'"
          @click="disableMfa"
          @mouseover.native="setMouseOverStatus(true)"
          @mouseleave.native="setMouseOverStatus(false)"
          >{{ buttonText }}</el-button
        >
      </h4>
    </div>
    <hr />

    <h4 style="margin: 20px 0 5px 0">Preferred 2FA Method</h4>
    <p style="margin-bottom: 0; font-size: 12px">
      Set your preferred method to use two-factor authentication
    </p>
    <el-row :gutter="20">
      <el-col :span="6">
        <dads-select
          @change="enableMFA"
          v-model="mode"
          placeholder="Select 2FA Method"
        >
          <el-option
            v-for="item in modeOptions"
            :key="item.label"
            :label="item.label"
            :value="item.value"
          />
        </dads-select>
      </el-col>
    </el-row>

    <MFAMethods :mode="mode" />
  </div>
</template>

<script>
import MFAMethods from "./mfa-methods.vue";
import DadsSelect from "@/components/select";
import { apiV2Host } from "@/feathers/feathers-client";
import { phoneNumberFormatter } from "@/globals/globals";

export default {
  name: "MFASettings",

  components: {
    DadsSelect,
    MFAMethods,
  },

  data() {
    return {
      processing: false,
      disableProcessing: false,
      forcedMfaByOrg: false,
      mode: "",
      isMouseOver: false,
    };
  },

  computed: {
    user() {
      return this.$store.state.auth.user;
    },

    mfaMode() {
      return this.user.mfaMode;
    },

    mfaPhoneNumber() {
      return this.user.mfaPhoneNumber;
    },

    mfaEnabled() {
      return this.user.mfaEnabled;
    },

    buttonText() {
      if (this.isMouseOver) {
        return "Disable";
      } else {
        return this.mfaEnabled ? "Enabled" : "Disable";
      }
    },

    displayPhoneNumber() {
      return this.mfaEnabled && this.mode === "sms" && this.mfaPhoneNumber;
    },

    headers() {
      return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
        Accept: "application/json",
      };
    },

    modeOptions() {
      const options = [
        {
          label: "Email",
          value: "email",
        },
      ];

      if (this.user.mfaAppVerified) {
        options.push({
          label: "Authenticator App",
          value: "app",
        });
      }

      if (this.user.mfaSmsVerified) {
        options.push({
          label: "SMS/Text Message",
          value: "sms",
        });
      }

      return options;
    },
  },

  watch: {
    mfaMode() {
      this.mode = this.mfaMode;
    }
  },

  methods: {
    setMouseOverStatus(status) {
      if (this.forcedMfaByOrg) return;
      this.isMouseOver = status;
    },

    numberFormatter(val) {
      return phoneNumberFormatter(val);
    },

    async refreshUserInfo() {
      try {
        await this.$store.dispatch("auth/authenticate", {
          strategy: "jwt",
          accessToken: this.$store.state.auth.accessToken,
        });
      } catch (_e) {
        //
      }
    },

    notifyGenericError() {
      this.$notify({
        title: "Something Went Wrong!",
        message: "Please try again later!",
        type: "error",
      });
    },

    errorOnApiCall(json = {}, notify = true) {
      const { code, name, message } = json;
      if (code && ["4", "5"].includes(`${code}`[0])) {
        if (notify) {
          this.$notify({
            title: name,
            message: message,
            type: "error",
          });
        }
        return true;
      }

      return false;
    },

    disableMfa() {
      if (this.forcedMfaByOrg) return;
      this.$confirm(
        "Turning off Two-Factor Authentication may reduce account security. You will  no longer receive a verification code to log in?",
        {}
      )
        .then(async () => {
          try {
            this.disableProcessing = true;
            const res = await fetch(`${apiV2Host}/mfa/auth/disable`, {
              method: "POST",
              body: JSON.stringify({}),
              headers: this.headers,
            });
            const json = await res.json();

            if (this.errorOnApiCall(json, false)) {
              this.notifyGenericError();
              return;
            }

            await this.refreshUserInfo();

            this.$notify({
              title: "Success",
              message: "Two-Factor Authentication disabled successfully!",
              type: "success",
            });
            this.disableProcessing = false;
            this.mode = "";
          } catch (_e) {
            this.disableProcessing = false;
            this.notifyGenericError();
          }
        })
        .catch(() => true);
    },

    async enableMFA() {
      this.processing = true;

      const body = {
        mode: this.mode,
      };

      try {
        const res = await fetch(`${apiV2Host}/mfa/auth/enable`, {
          method: "POST",
          body: JSON.stringify(body),
          headers: this.headers,
        });

        const json = await res.json();

        if (this.errorOnApiCall(json)) {
          this.processing = false;
          this.mode = this.mfaMode;
          return;
        }

        await this.refreshUserInfo();
        this.processing = false;

        this.$notify({
          title: "Success",
          type: "success",
          message: "Successfully changed 2FA Method!",
        });
      } catch (_e) {
        this.mode = this.mfaMode;
        this.notifyGenericError();
        this.processing = false;
      }
    },
  },

  async created() {
    if (this.user) {
      const { mfaMode, mfaEnabled, tenantId } = this.user;
      this.mode = mfaEnabled ? this.user.mfaMode : "";

      try {
        const res = await fetch(`${apiV2Host}/tenants/${tenantId}/settings`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
            Accept: "application/json",
          },
        });
        const userTenant = await res.json();
        this.forcedMfaByOrg = userTenant.mfaEnabled === 1;
      } catch (_e) {
        //
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.disable-hover-effect {
  &:hover {
    background-color: #f92120 !important;
  }
  &:focus {
    background-color: #46CB63 !important;
  }
}

.disable-hover-disabled {
  &:hover {
    background-color: #46CB63 !important;
  }
  &:focus {
    background-color: #46CB63 !important;
  }
  cursor: not-allowed;
}

.el-col {
  position: relative;
}

.el-row {
  display: flex;
  align-items: center;
}

@media (max-width: 860px) {
  .el-row {
    flex-wrap: wrap;

    .el-col {
      width: 100%;
      max-width: 300px;
    }
  }
}

form {
  max-width: 600px;
  margin: 0 auto;
}
</style>
